import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Analytics } from '../lib/analytics';

/**
 * Custom hook to track page views automatically
 */
export const usePageTracking = () => {
  const location = useLocation();

  useEffect(() => {
    // Get page title from document or route
    const pageTitle = document.title || getPageTitleFromPath(location.pathname);
    
    // Track page view
    Analytics.trackPageView(location.pathname + location.search, pageTitle);
  }, [location]);
};

/**
 * Helper function to get readable page titles from paths
 */
const getPageTitleFromPath = (pathname: string): string => {
  const pathMap: Record<string, string> = {
    '/': 'Dashboard',
    '/login': 'Login',
    '/register': 'Register', 
    '/map': 'Map',
    '/builder': 'Plan Builder',
    '/find': 'Interactive Map Planner',
    '/profile': 'My Profile',
    '/settings': 'Settings', // Kept for backward compatibility
    '/whats-new': 'What\'s New',
    '/integrations': 'Integrations',
    '/stanton/whitelist': 'Admin - Email Whitelist'
  };

  // Handle dynamic routes
  if (pathname.startsWith('/plans/')) {
    return 'Plan View';
  }

  return pathMap[pathname] || 'Mindara';
};

/**
 * Hook to track user sessions and set user properties
 */
export const useUserTracking = () => {
  useEffect(() => {
    // Set basic user properties
    Analytics.setUserProperties({
      // We'd really like to tie this to a commit (e.g. 1e0cbe41)
      // app_version: process.env.REACT_APP_VERSION || '1.0.0',
      environment: process.env.NODE_ENV,
      platform: 'web',
      user_agent: navigator.userAgent,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      screen_resolution: `${window.screen.width}x${window.screen.height}`,
      viewport_size: `${window.innerWidth}x${window.innerHeight}`
    });
  }, []);
};

/**
 * Hook to set user ID when authenticated
 */
export const useUserIdentification = (userId?: string | null) => {
  useEffect(() => {
    if (userId) {
      Analytics.setUserId(userId);
    }
  }, [userId]);
};

const analyticsHooks = { usePageTracking, useUserTracking };
export default analyticsHooks;